import React, { useEffect, useState } from "react";
import BaseTable from "../../Components/Common/BaseTable"; 
import BaseButton from "../../Components/Common/BaseButton"; 
import Icon from '@mdi/react';
import { mdiPlus } from '@mdi/js';
import "../../STYLE/ListOfProducts.css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {REQUEST_DELETE_PROJECT_COMMON, REQUEST_LIST_OF_PROJECTS_COMMON } from "API/Services/common"; 
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner } from "reactstrap";
import { ACTION_HEADER, ACTIONS_ACCESSOR, ADD_LABEL, ARE_YOU_SURE_DELETE, CANCEL_LABEL, DELETE_CAPI, DELETE_LABEL, EDIT_CAPI, PROJECT_NAME_LABEL, PROJECTNAME_ACCESSOR, PROJECTS_LABEL, PROJECTS_TITLE, SEARCH } from "Components/constants/commonNames";
import { checkStatusCodeSuccess } from "Components/constants/enums";

interface Project {
  id: number;
  project_name: string;
  projectImages: { id: number, project_image: string }[];
}

interface PaginationInfo {
  totalPages: number;
  totalRecordsCount: number;
  currentPage: number;
  numberOfRows: any | number ;
}
const ListOfProjects = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [modal, setModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [columnName, setColumnName] = useState("id");
  const [sortOrder, setSortOrder] = useState("desc");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo>({
    totalPages: 1,
    totalRecordsCount: 0,
    currentPage: 1,
    numberOfRows: 10,
  });
  const [loader, setLoader] = useState(false);
  const [fullScreenLoader, setFullScreenLoader] = useState(false);

  const navigate = useNavigate();
  
  const columns = [
    {
      id: "serialNumber",
      header: "S.No", 
      cell: (cell: any) => {
        const serialNumber = (paginationInfo.currentPage - 1) * paginationInfo.numberOfRows + (cell.row.index + 1);
        return serialNumber;
      },
    },
    {
      enableColumnFilter: false,
      accessorKey: PROJECTNAME_ACCESSOR,
      header: PROJECT_NAME_LABEL,
      cell: (cell: any) => cell?.row?.original?.project_name || "No name available",
    },
    {
      id: ACTIONS_ACCESSOR,
      header: ACTION_HEADER,
      cell: ({ row }: any) => (
        <div className="d-flex">
          <i
            className="text-info mdi mdi-pencil fs-4 cursor-pointer"
            onClick={() => navigate(`/edit-project/${row.original.id}`)}
            title={EDIT_CAPI}
          />
          <span className="mx-2" />
          <i
            className="text-danger mdi mdi-delete fs-4 cursor-pointer "
            onClick={() => confirmDelete(row.original)}
            title={DELETE_CAPI}
          />
        </div>
      ),
    },
  ];
  
  const payload = {
    searchBar: searchValue,
    sortKey: columnName,
    sortValue: sortOrder,
    pageSize: pageSize,
    page: page,
  };

  const fetchProjects = async () => {
    setFullScreenLoader(true);
    try {
      const response = await REQUEST_LIST_OF_PROJECTS_COMMON(payload);

      if (checkStatusCodeSuccess(response?.statusCode)) {
        setProjects(response.data.listOfProject);

        setPaginationInfo({
          totalPages: response.data.totalPages,
          totalRecordsCount: response.data.totalRecordsCount,
          currentPage: response.data.currentPage,
          numberOfRows: response.data.numberOfRows
        });
      } else {
        setPaginationInfo(prev => ({
          ...prev,
          numberOfRows: 0,
        }));
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error.message);
    } finally {
      setFullScreenLoader(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [searchValue, columnName, sortOrder, pageSize, page]);

  const handleEdit = (projectId: number) => {
    navigate(`/edit-project/${projectId}`);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  const handleSearch = (value: string) => {
    if (value !== searchValue) {
      setPage(1);
      setSearchValue(value);
    }
  };

  const handleSort = (column: string, direction: 'asc' | 'desc') => {
    setColumnName(column);
    setSortOrder(direction);
    setPage(1);
  };

  const confirmDelete = (project: Project) => {
    setProjectToDelete(project);
    toggleModal();
  };

  const toggleModal = () => {
    setModal(!modal);
  };

  const updateProjectStatus = async () => {
    if (projectToDelete) {
      toggleModal();
      setFullScreenLoader(true); 
  
      try {
        const response = await REQUEST_DELETE_PROJECT_COMMON(projectToDelete.id);
  
        if (checkStatusCodeSuccess(response.statusCode)) {
          toast.success(response?.message);
          setProjects(projects.filter(project => project.id !== projectToDelete.id))
        } else {
          throw new Error(response.message || "Failed to delete project");
        }
      } catch (error: any) {
        toast.error(error.message || "Error deleting project");
      } finally {
        setFullScreenLoader(false);
        setProjectToDelete(null);
      }
    }
  };
  

  document.title = PROJECTS_TITLE;

  return (
    <div className="top-div">
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center mb-1 mt-2">
          <span className="fs-4 mx-2">{PROJECTS_LABEL}</span>
          <BaseButton
            onClick={() => navigate("/add-project")}
            className="mt-0 w-auto px-4 py-2"
            color="info"
            text={<>
              <Icon path={mdiPlus} size={0.8} className="me-2"/>
              {ADD_LABEL}
            </>}
          />
        </div>

        <div className="card">
          <div className="card-body">
            <BaseTable
              columns={columns}
              data={projects}
              isGlobalFilter={true}
              customPageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={handlePageSizeChange}
              onSearch={handleSearch}
              onSort={handleSort}
              tableClass="table bg-white"
              divClass="text-nowrap"
              manualPagination={true}
              tableLoader={loader}
              numberOfRows={paginationInfo.numberOfRows}
              currentPage={paginationInfo.currentPage}
              totalPages={paginationInfo.totalPages}
              totalRecords={paginationInfo.totalRecordsCount}
              SearchPlaceholder={SEARCH}
            />
          </div>
        </div>
      </div>

      <Modal
        isOpen={modal}
        toggle={toggleModal}
        className="modal-dialog-centered modal-md"
      >
        <ModalHeader toggle={toggleModal}>{DELETE_LABEL}</ModalHeader>
        <ModalBody>
          <p>{ARE_YOU_SURE_DELETE}</p>
        </ModalBody>
        <ModalFooter>
          <Button color="success" onClick={updateProjectStatus}>
            {DELETE_LABEL}
          </Button>
          <Button color="danger" onClick={toggleModal}>
            {CANCEL_LABEL}
          </Button>
        </ModalFooter>
      </Modal>

      {fullScreenLoader && ( 
        <div className="full-screen-loader">
          <Spinner />
        </div>
      )}
    </div>
  );
};

export default ListOfProjects;
